"use client";

import { useJsApiLoader } from "@react-google-maps/api";
import { useEffect, useState } from "react";
import usePlacesAutocomplete, { getGeocode } from "use-places-autocomplete";
import { capitalize } from "es-toolkit/string";
import { PlacesAutoCompleteAddressResult } from "types/common";
import { GOOGLE_LIBRARIES } from "utils/constants";

const GOOGLE_PLACES_KEY = process.env.NEXT_PUBLIC_GOOGLE_PLACES_KEY;

const formatAddressForPlace = ({ geometry, ...place }: { geometry: any; address_components: any }) => {
  const addressComponents = place.address_components;
  let addressLine1 = "";
  let addressLine2 = "";
  let city = "";
  let state = "";
  let zipCode = "";
  for (const component of addressComponents) {
    const componentType = component.types[0];
    switch (componentType) {
      case "street_number": {
        addressLine1 = `${component.long_name} ${addressLine1}`;
        break;
      }
      case "route": {
        addressLine1 += component.long_name;
        break;
      }
      case "subpremise": {
        addressLine2 += capitalize(`${component.long_name}`);
        break;
      }
      case "postal_code": {
        zipCode = component.long_name;
        break;
      }
      case "locality": {
        city = component.long_name;
        break;
      }
      case "administrative_area_level_1": {
        state = component.short_name;
        break;
      }
      default:
        break;
    }
  }

  return {
    addressLine1,
    addressLine2,
    city,
    state,
    zipCode,
    longitude: geometry?.location?.lng(),
    latitude: geometry?.location?.lat()
  };
};

export default function useAddressAutocomplete(updateAddress: (newAddress: PlacesAutoCompleteAddressResult) => void) {
  const [isInitialized, setIsInitialized] = useState(false);
  const {
    suggestions: { data, status },
    clearSuggestions,
    setValue,
    init
  } = usePlacesAutocomplete({
    initOnMount: false,
    debounce: 300,
    requestOptions: {
      types: ["address"],
      componentRestrictions: { country: "us" }
    }
  });

  const isRequestError = [
    "REQUEST_DENIED",
    "INVALID_REQUEST",
    "OVER_QUERY_LIMIT",
    "UNKNOWN_ERROR",
    "ZERO_RESULTS"
  ].includes(status);
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_PLACES_KEY as string,
    libraries: GOOGLE_LIBRARIES
  });

  useEffect(() => {
    if (isLoaded && !isInitialized) {
      setIsInitialized(true);
      init();
    }
  }, [isLoaded, init, isInitialized]);

  const onSelect = (placeId?: string | null) => {
    if (placeId) {
      clearSuggestions();
      getGeocode({ placeId })
        .then((results: any) => {
          if (results?.[0]) {
            updateAddress(formatAddressForPlace(results[0]));
          }
        })
        .catch((e: any) => console.error(e));
    } else {
      updateAddress({});
    }
  };

  return {
    suggestions: data,
    onChangeInput: setValue,
    onSelect,
    scriptLoadError: loadError,
    scriptLoaded: isLoaded,
    isRequestError
  };
}
